import React, { useState } from 'react';
import {
  Box, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableRow, Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { poco } from '../../theme/vidro';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import NivelChip from './NivelChip';

/**
 * A régua de um indicador: os quatro degraus da faixa e de onde eles vêm.
 *
 * Receber "Atenção" num indicador que não se conhece informa que algo está mal
 * e esconde o que seria estar bem. Aqui o critério aparece inteiro, com o
 * degrau atual destacado — o usuário vê quanto falta para o degrau de cima, que
 * é a única pergunta que ele realmente tem.
 */
const Regua = ({ regua, nivelAtual }) => {
  if (!regua) return null;
  const nivelDoRotulo = {
    'Crítico': 'critico',
    'Atenção': 'atencao',
    'Bom': 'bom',
    'Excelente': 'excelente'
  };

  return (
    <Box sx={{ py: 1.5, pl: 0.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {regua.degraus.map((degrau) => {
          const atual = nivelDoRotulo[degrau.rotulo] === nivelAtual;
          return (
            <Box
              key={degrau.rotulo}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: atual ? 'primary.main' : 'divider',
                bgcolor: atual ? 'action.selected' : 'transparent',
                minWidth: 108
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: atual ? 700 : 500, display: 'block' }}>
                {degrau.rotulo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {degrau.valor}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {regua.fonte && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, maxWidth: '70ch' }}>
          {regua.fonte}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Os indicadores de apoio de um bloco, em tabela densa.
 *
 * Eles ficam MENORES que o número protagonista de propósito: quando todo
 * indicador tem o mesmo peso visual, nenhum tem. Aqui a hierarquia é
 * explícita — o bloco tem uma manchete e esta lista é o rodapé dela.
 *
 * Valor ausente mostra `—` e, embaixo, o motivo. Nunca `R$ 0,00`: zero é uma
 * medição, e afirmar uma medição que ninguém fez é o pior defeito possível
 * neste produto.
 */
const ListaDeIndicadores = ({ itens = [], dense = true }) => {
  const [aberto, setAberto] = useState(null);
  if (!itens.length) return null;

  return (
    // A rolagem horizontal é da TABELA, nunca da página: no celular, o corpo
    // do documento rolando de lado faz o usuário perder a coluna de rótulos.
    //
    // `overflowY` PRECISA vir declarado. Pelo CSS, um eixo em `auto` empurra o
    // outro de `visible` para `auto`, e a tabela sobra 1 ou 2 px sobre a caixa
    // por arredondamento de linha — o bastante para o navegador desenhar uma
    // barra de rolagem vertical dentro do cartão, com setas e tudo. No Windows,
    // onde a barra é opaca, o painel inteiro ganhava trilhos que não rolavam
    // nada. Medido: +1px em Resultado e Estrutura de custos, +2px em Custo da
    // dívida.
    // A LISTA É O MOSTRADOR, e o cartão em volta é o chassi.
    //
    // Ela passa a viver num poço escavado: o dado fica rebaixado em relação ao
    // texto que o explica, e o olho separa "o que é leitura" de "o que é
    // medição" antes de ler uma palavra. É a segunda superfície do mundo, e a
    // única — duas bastam; três viram decoração.
    <Box
      sx={(t) => ({
        ...poco(t.palette.mode === 'dark'),
        px: { xs: 1.5, sm: 2 },
        py: 0.5,
        overflowX: 'auto',
        overflowY: 'hidden'
      })}
    >
    <Table size={dense ? 'small' : 'medium'} aria-label="Indicadores do bloco" sx={{ minWidth: 320 }}>
      <TableBody>
        {itens.map((item) => (
          <React.Fragment key={item.chave}>
          <TableRow
            sx={{
              '&:last-child td': { borderBottom: 0 },
              // A LINHA INTEIRA REAGE, e não só a que abre régua.
              //
              // Numa lista de oito indicadores com o nome à esquerda e o valor a
              // 900px de distância, o olho perde a linha no meio do caminho. O
              // realce sob o ponteiro é a régua que o dedo não tem — ele liga
              // rótulo e número sem que nada precise ser desenhado entre os dois.
              transition: 'background-color 140ms cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { bgcolor: 'action.hover' },
              // A LINHA CRÍTICA É TINGIDA, A 7%.
              //
              // Numa lista de oito indicadores o chip vermelho fica na ponta
              // direita, a 900px do nome: para achar o que está ruim, o olho
              // varre a coluna da direita inteira e volta. O tingimento marca a
              // linha INTEIRA, então a varredura acha o problema onde ela
              // começa. Não é cor sozinha — o chip com ícone e rótulo continua
              // lá, e é ele que informa quem não distingue vermelho.
              //
              // Foi um filete de 2px na margem esquerda antes disto, e ele era
              // invisível: a caixa da tabela tem `overflow` declarado nos dois
              // eixos por causa da rolagem horizontal, e recortava qualquer
              // coisa em deslocamento negativo.
              ...(item.nivel === 'critico'
                ? {
                    bgcolor: (t) => alpha(t.palette.error.main, t.palette.mode === 'dark' ? 0.07 : 0.05),
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.error.main, t.palette.mode === 'dark' ? 0.12 : 0.09)
                    }
                  }
                : {}),
              ...(item.regua ? { cursor: 'pointer' } : {})
            }}
            onClick={item.regua ? () => setAberto(aberto === item.chave ? null : item.chave) : undefined}
          >
            <TableCell sx={{ pl: 0, verticalAlign: 'top', borderColor: 'divider' }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{item.nome}</Typography>
                {item.regua && (
                  <IconButton
                    size="small"
                    aria-label={`Ver a régua de ${item.nome}`}
                    aria-expanded={aberto === item.chave}
                    sx={{
                      p: 0.25,
                      // O ÍCONE MEDIA 20x20px — menos da metade do mínimo de
                      // 44px, e ele é a única affordance visível da régua. A
                      // linha inteira também abre, mas ninguém adivinha isso: o
                      // que a pessoa mira é a setinha. O alvo cresce por
                      // pseudo-elemento, e não por padding, para que a densidade
                      // da tabela não mude — a lista de apoio é densa de
                      // propósito, e engordar a linha custaria uma dobra.
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 44,
                        height: 44,
                        transform: 'translate(-50%, -50%)'
                      },
                      transform: aberto === item.chave ? 'rotate(180deg)' : 'none',
                      transition: 'transform 180ms ease-out'
                    }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                )}
              </Stack>
              {item.motivo && (
                <Typography variant="caption" color="text.secondary" component="p">
                  {item.motivo}
                </Typography>
              )}
            </TableCell>

            {/* O CONDUTOR PONTILHADO — o recurso que balanço impresso usa há um
                século para o mesmo problema que esta tela tem.
                Numa área de trabalho de 1600px o nome fica na esquerda e o
                número na direita, e o olho perde a linha no meio do caminho. A
                resposta anterior foi estreitar a página inteira para 960px, o
                que trocou um defeito por outro maior: um terço da tela vazio de
                cada lado, num produto cuja régua é terminal de mercado.
                O pontilhado liga os dois sem estreitar nada, e é a única peça
                decorativa do produto que carrega função. Ele some no celular,
                onde a distância já é curta e o traço vira sujeira. */}
            <TableCell
              aria-hidden="true"
              sx={{
                width: '100%',
                p: 0,
                verticalAlign: 'bottom',
                display: { xs: 'none', sm: 'table-cell' }
              }}
            >
              <Box
                sx={{
                  mx: 1.5,
                  mb: '0.42em',
                  borderBottom: '1px dotted',
                  borderColor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,34,0.18)'
                }}
              />
            </TableCell>
            <TableCell align="right" sx={{ pr: 0, pl: 0, whiteSpace: 'nowrap', width: 1, borderColor: 'divider' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.5, sm: 1.5 }}
                alignItems={{ xs: 'flex-end', sm: 'center' }}
                justifyContent="flex-end"
              >
                <Box
                  className="valor-numerico"
                  sx={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    letterSpacing: '-0.012em',
                    // O TRAVESSÃO É O SÍMBOLO CENTRAL DESTE PRODUTO, e ele era o
                    // texto MENOS legível da tela: `text.disabled` mediu 1,47:1
                    // no tema claro e 2,27:1 no escuro, contra o mínimo de
                    // 4,5:1. "Ausência é travessão" não significa "ausência é
                    // invisível" — o traço tem de ser LIDO, senão a linha parece
                    // vazia e o usuário conclui que o produto quebrou.
                    color: item.valor === null ? 'text.secondary' : 'text.primary'
                  }}
                >
                  {item.texto}
                </Box>
                {item.nivel !== 'semFaixa' && item.nivel !== 'ausente' && (
                  <NivelChip
                    nivel={item.nivel}
                    rotulo={item.rotulo}
                    cor={item.cor}
                    fonte={item.fonte}
                  />
                )}
              </Stack>
            </TableCell>
          </TableRow>
          {item.regua && (
            <TableRow>
              <TableCell colSpan={3} sx={{ px: 0, py: 0, borderBottom: 0 }}>
                <Collapse in={aberto === item.chave} timeout={180} unmountOnExit>
                  <Regua regua={item.regua} nivelAtual={item.nivel} />
                </Collapse>
              </TableCell>
            </TableRow>
          )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
    </Box>
  );
};

export default ListaDeIndicadores;
